import { lowerCase } from 'es-toolkit';

import { FROM_TYPE_CONFIG, FromTypeConfig } from '@/lib/constants/simple-auth-from-type';
import { SimpleAuthCertificateType, SimpleAuthFromType } from '@/lib/types/simple-auth';

export class FromTypeManager {
  static getConfig(fromType: SimpleAuthFromType): FromTypeConfig {
    return FROM_TYPE_CONFIG[fromType];
  }

  /**
   * Generate event name using the pattern: indis-{eventPrefix}_{action}
   */
  static generateEventName(fromType: SimpleAuthFromType, action: string): string {
    const config = this.getConfig(fromType);
    return action ? `indis-${config.eventPrefix}_${action}` : `indis-${config.eventPrefix}`;
  }

  /**
   * 나가기 dialog 문구
   * */
  static getExitDialogTitle(fromType: SimpleAuthFromType): string {
    return this.getConfig(fromType).exitDialog.title;
  }

  /**
   * 나가기 dialog를 통한 이탈시 이동할 페이지
   * */
  static getExitPath(fromType: SimpleAuthFromType): string {
    return this.getConfig(fromType).exitDialog.exitPath;
  }

  /**
   * 간편인증 인증확인 페이지 이후 플로우 path
   * */
  static getConfirmNextPath(fromType: SimpleAuthFromType): string {
    return this.getConfig(fromType).confirmPage.nextPath;
  }

  /**
   * 간편인증 인증확인 페이지 탑네비게이션 나가기 버튼 유무
   */
  static hasExitButton(fromType: SimpleAuthFromType): boolean {
    return this.getConfig(fromType).features.hasExitButton;
  }

  /**
   * 간편인증 탑네비게이션 나가기 모달 events
   * kbank(정보입력)와 인증확인 페이지 이벤트 구분없음
   * alimtalk 타입은 exit dialog 미사용
   */
  static getExitDialogEvents(fromType: SimpleAuthFromType) {
    if (fromType === 'alimtalk') return { view: '', clickExit: '', clickKeep: '' };
    return {
      view: this.generateEventName(fromType, 'confirm_close-dialog'),
      clickExit: this.generateEventName(fromType, 'confirm_close-ok'),
      clickKeep: this.generateEventName(fromType, 'confirm_close-cancel')
    };
  }

  /**
   * 간편인증 정보입력 페이지 이벤트
   * */
  static getInputPageEvents(fromType: SimpleAuthFromType) {
    return {
      view: this.generateEventName(fromType, fromType === 'lookup' ? '' : 'simplecert'),
      clickBack: this.generateEventName(fromType, 'back'),
      clickCx: this.generateEventName(fromType, 'cx_top-right'),
      clickCta: this.generateEventName(fromType, 'next')
    };
  }

  /**
   * 간편인증 정보입력 페이지 form field 클릭(focus) 이벤트
   * */
  static getInputFormClickEvent(fromType: SimpleAuthFromType) {
    return {
      name: this.generateEventName(fromType, 'simplecert-input_name'),
      birthday: this.generateEventName(fromType, 'simplecert-input_birth-date'),
      phone: this.generateEventName(fromType, 'simplecert-input_phonenumber')
    };
  }

  /**
   * 간편인증 정보입력 페이지 카카오/네이버 인증수단 click event
   * */
  static getCertTypeClickEvent(fromType: SimpleAuthFromType, certType: SimpleAuthCertificateType) {
    return this.generateEventName(fromType, `simplecert-input_type-${lowerCase(certType)}`);
  }

  /**
   * 간편인증 인증확인 페이지 events
   * */
  static getConfirmPageEvents(fromType: SimpleAuthFromType) {
    return {
      view: this.generateEventName(fromType, 'confirm'),
      clickExit: this.generateEventName(fromType, 'confirm_close'),
      clickCheckForm: this.generateEventName(fromType, 'confirm_inform-check'),
      clickRetry: this.generateEventName(fromType, 'confirm_retry'),
      clickCta: this.generateEventName(fromType, 'confirm_next')
    };
  }

  /**
   * 네이버 앱 미설치 안내 drawer events
   * */
  static getNaverAppInstallDrawerEvent(fromType: SimpleAuthFromType) {
    return {
      view: this.generateEventName(fromType, 'device-not-found'),
      clickOk: this.generateEventName(fromType, 'device-not-found_ok'),
      clickGuide: this.generateEventName(fromType, 'device-not-found_cx-guide')
    };
  }

  /**
   * 홈택스유저아님 에러 dialog events
   * */
  static getNoHometaxUserErrorEvents(fromType: SimpleAuthFromType) {
    return {
      view: this.generateEventName(fromType, 'confirm_error-nouser'),
      clickRetry: this.generateEventName(fromType, 'confirm_error-nouser-retry'),
      clickHometax: this.generateEventName(fromType, 'error-nouser-hometax')
    };
  }

  /**
   * 기존 tin과 비일치 에러 dialog events
   * */
  static getNotEqualTinErrorEvents(fromType: SimpleAuthFromType) {
    return {
      view: this.generateEventName(fromType, 'confirm_error-tin'),
      clickOk: this.generateEventName(fromType, 'confirm_error-tin-retry')
    };
  }

  /**
   * 기신청 에러 dialog events
   * */
  static getAlreadyAppliedEvents(fromType: SimpleAuthFromType) {
    return {
      view: this.generateEventName(fromType, 'confirm_sametin'),
      clickCx: this.generateEventName(fromType, 'confirm_sametin-cx'),
      clickOk: this.generateEventName(fromType, 'confirm_sametin-ok')
    };
  }

  /**
   * 다른계정 환급 완료 에러 dialog events
   * */
  static getAlreadyCompletedEvents(fromType: SimpleAuthFromType) {
    return {
      view: this.generateEventName(fromType, 'confirm_sametin-nodata'),
      clickOk: this.generateEventName(fromType, 'confirm_sametin-nodata-ok')
    };
  }
}

export const useFromTypeStaticData = (fromType: SimpleAuthFromType) => {
  const config = FromTypeManager.getConfig(fromType);

  return {
    config,
    hasExitButton: FromTypeManager.hasExitButton(fromType),
    getExitDialogTitle: () => FromTypeManager.getExitDialogTitle(fromType),
    getExitPath: () => FromTypeManager.getExitPath(fromType),
    getExitDialogEvents: () => FromTypeManager.getExitDialogEvents(fromType),
    getInputPageEvents: () => FromTypeManager.getInputPageEvents(fromType),
    getInputFormClickEvent: () => FromTypeManager.getInputFormClickEvent(fromType),
    getCertTypeClickEvent: (certType: SimpleAuthCertificateType) => FromTypeManager.getCertTypeClickEvent(fromType, certType),
    getConfirmPageEvents: () => FromTypeManager.getConfirmPageEvents(fromType),
    getConfirmNextPath: () => FromTypeManager.getConfirmNextPath(fromType),
    getNaverAppInstallDrawerEvent: () => FromTypeManager.getNaverAppInstallDrawerEvent(fromType),
    getNoHometaxUserErrorEvents: () => FromTypeManager.getNoHometaxUserErrorEvents(fromType),
    getNotEqualTinErrorEvents: () => FromTypeManager.getNotEqualTinErrorEvents(fromType),
    getAlreadyAppliedEvents: () => FromTypeManager.getAlreadyAppliedEvents(fromType),
    getAlreadyCompletedEvents: () => FromTypeManager.getAlreadyCompletedEvents(fromType)
  };
};
